import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, X, Check } from "lucide-react";
import { TopNav } from "../components/layout/top-nav";
import { useAuth } from "../hooks/use-auth";
import { ConfirmDialog } from "../components/confirm-dialog";

const models = [
  { id: "deepseek-v3", name: "DeepSeek V3", multimodal: false },
  { id: "deepseek-v2", name: "DeepSeek V2", multimodal: false },
  { id: "hunyuan", name: "混元", multimodal: false },
  { id: "gpt-4o", name: "GPT-4o (多模态)", multimodal: true },
  { id: "gpt-4o-mini", name: "GPT-4o Mini (多模态)", multimodal: true },
  { id: "qwen-vl-max", name: "通义千问 VL (多模态)", multimodal: true },
  { id: "glm-4v", name: "GLM-4V (多模态)", multimodal: true },
];

interface Persona {
  id: string;
  icon: string;
  name: string;
  role?: string;
  description: string;
  model: string;
  isDefault?: boolean;
}

export function PersonaPage() {
  const auth = useAuth();
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    name: string;
    description: string;
    model: string;
  }>({ name: "", description: "", model: "" });
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);

  // 默认人格
  const defaultPersonas: Persona[] = [
    { id: "pm", icon: "📝", name: "产品经理", role: "需求分析 · 文档撰写", description: "帮你梳理产品逻辑，撰写专业PRD", model: "deepseek-v3", isDefault: true },
    { id: "designer", icon: "🎨", name: "设计师", role: "创意灵感 · 视觉方案", description: "品牌设计、UI建议、创意发散", model: "deepseek-v3", isDefault: true },
    { id: "developer", icon: "💻", name: "程序员", role: "代码优化 · 技术实现", description: "代码审查、重构建议、技术方案", model: "deepseek-v3", isDefault: true },
    { id: "analyst", icon: "📊", name: "分析师", role: "数据分析 · 商业洞察", description: "商业模式分析、数据解读、策略建议", model: "deepseek-v3", isDefault: true },
    { id: "mentor", icon: "🎓", name: "导师", role: "面试辅导 · 技能提升", description: "模拟面试、职业规划、学习路径", model: "deepseek-v3", isDefault: true },
    { id: "translator", icon: "🌐", name: "翻译官", role: "多语言 · 本地化", description: "精准翻译、文化适配、润色优化", model: "deepseek-v3", isDefault: true },
  ];

  useEffect(() => {
    // 加载已删除的预设角色列表
    const deletedDefaults = JSON.parse(localStorage.getItem("deletedDefaultPersonas") || "[]");
    // 过滤掉已删除的预设角色
    const filteredDefaults = defaultPersonas.filter(p => !deletedDefaults.includes(p.id));
    
    // 加载自定义人格
    const saved = localStorage.getItem("customPersonas");
    const customPersonas = saved ? JSON.parse(saved) : [];
    setPersonas([...filteredDefaults, ...customPersonas]);
  }, []);

  // 开始编辑
  const startEdit = (persona: Persona) => {
    setEditingId(persona.id);
    setEditForm({
      name: persona.name,
      description: persona.description,
      model: persona.model,
    });
  };

  // 保存编辑
  const saveEdit = () => {
    if (!editingId) return;
    
    const updatedPersonas = personas.map(p => {
      if (p.id === editingId) {
        return { ...p, ...editForm };
      }
      return p;
    });
    
    // 更新本地存储（只更新自定义人格）
    const customPersonas = updatedPersonas.filter(p => !p.isDefault);
    localStorage.setItem("customPersonas", JSON.stringify(customPersonas));
    
    setPersonas(updatedPersonas);
    setEditingId(null);
  };

  // 取消编辑
  const cancelEdit = () => {
    setEditingId(null);
  };

  // 删除人格
  const deletePersona = (id: string) => {
    const updatedPersonas = personas.filter(p => p.id !== id);
    
    // 保存删除后的列表到 localStorage
    // 包括删除预设角色后的状态
    const deletedDefaults = JSON.parse(localStorage.getItem("deletedDefaultPersonas") || "[]");
    const persona = personas.find(p => p.id === id);
    if (persona?.isDefault) {
      deletedDefaults.push(id);
      localStorage.setItem("deletedDefaultPersonas", JSON.stringify(deletedDefaults));
    }
    
    // 更新自定义人格
    const customPersonas = updatedPersonas.filter(p => !p.isDefault);
    localStorage.setItem("customPersonas", JSON.stringify(customPersonas));
    
    setPersonas(updatedPersonas);
    setDeleteConfirm(null);
  };

  // 获取模型名称
  const getModelName = (modelId: string) => {
    return models.find(m => m.id === modelId)?.name || modelId;
  };

  return (
    <div className="min-h-screen bg-white">
      <TopNav user={auth.user} onSignOut={auth.user ? auth.signOut : undefined} />
      
      <main className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            返回首页
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">我的人格</h1>
          <p className="text-gray-500 text-sm">
            管理你的 AI 角色，编辑模型绑定和描述
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {personas.map((persona) => {
            const isEditing = editingId === persona.id;
            
            return (
              <div
                key={persona.id}
                className={`bg-white border rounded-xl p-5 transition-all ${
                  isEditing ? "border-blue-300 shadow-md" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                {isEditing ? (
                  // 编辑模式
                  <div className="space-y-4">
                    {/* 头像和名称 */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gray-100 overflow-hidden">
                        {persona.icon.startsWith("http") || persona.icon.startsWith("data:") ? (
                          <img src={persona.icon} alt={persona.name} className="w-full h-full object-cover" />
                        ) : (
                          persona.icon
                        )}
                      </div>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-gray-900 font-medium outline-none focus:border-blue-400"
                        placeholder="角色名称"
                      />
                    </div>
                    
                    {/* 模型选择 */}
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">绑定模型</label>
                      <select
                        value={editForm.model}
                        onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-blue-400"
                      >
                        {models.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name} {model.multimodal && "🖼️"}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* 描述 */}
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">描述</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 outline-none focus:border-blue-400 resize-none"
                        rows={2}
                        placeholder="角色描述"
                      />
                    </div>
                    
                    {/* 操作按钮 */}
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={cancelEdit}
                        className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700"
                      >
                        取消
                      </button>
                      <button
                        onClick={saveEdit}
                        className="px-4 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-1"
                      >
                        <Check className="w-4 h-4" />
                        保存
                      </button>
                    </div>
                  </div>
                ) : (
                  // 显示模式
                  <div>
                    <div className="flex items-start gap-3 mb-3">
                      {/* 头像 */}
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl bg-gray-100 overflow-hidden shrink-0">
                        {persona.icon.startsWith("http") || persona.icon.startsWith("data:") ? (
                          <img src={persona.icon} alt={persona.name} className="w-full h-full object-cover" />
                        ) : (
                          persona.icon
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{persona.name}</h3>
                          {persona.isDefault && (
                            <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded-full">
                              预设
                            </span>
                          )}
                        </div>
                        {persona.role && (
                          <p className="text-xs text-purple-600 mt-0.5">{persona.role}</p>
                        )}
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{persona.description}</p>
                    
                    {/* 模型标签 */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">模型:</span>
                        <span className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full">
                          {getModelName(persona.model)}
                        </span>
                      </div>
                      
                      {/* 操作按钮 */}
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => startEdit(persona)}
                          className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition"
                          title="编辑"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm({ id: persona.id, name: persona.name })}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                          title="删除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {personas.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            还没有角色，去首页创建一个吧
          </div>
        )}
      </main>

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        isOpen={deleteConfirm !== null}
        title="删除角色"
        message={`确定要删除"${deleteConfirm?.name}"吗？`}
        onConfirm={() => {
          if (deleteConfirm) {
            deletePersona(deleteConfirm.id);
          }
        }}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
